import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
    updateTicketStatusAPI, 
    fetchSupportStaffAPI,
    fetchTicketsForExportAPI 
} from '../services/serverService';
import { db } from '../firebase';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { FaBug, FaLightbulb, FaFileInvoiceDollar, FaQuestionCircle, FaUserTag, FaDownload, FaSpinner } from 'react-icons/fa';
import RoutingRulesModal from '../components/RoutingRulesModal';
import TicketDetailModal from '../components/TicketDetailModal';

const COLUMNS = [
    { id: 'Open', title: 'Open', color: '#fff5f5', bar: '#9b2c2c' },
    { id: 'In-Progress', title: 'In Progress', color: '#ebf8ff', bar: '#3182ce' },
    { id: 'On-Hold', title: 'On Hold', color: '#fffaf0', bar: '#ed8936' },
    { id: 'Waiting on Customer', title: 'Waiting on Customer', color: '#faf5ff', bar: '#805ad5' },
    { id: 'Resolved', title: 'Resolved', color: '#f0fff4', bar: '#38a169' },
    { id: 'Archived', title: 'Archived', color: '#f7fafc', bar: '#718096' }
];

export default function SupportDashboardPage() {
    const { currentUser, isSupportManager, userRole } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const loginUsername = localStorage.getItem('loginUsername');
    
    // Core Data States
    const [tickets, setTickets] = useState([]);
    const [staff, setStaff] = useState([]);
    const [supportStaff, setSupportStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTicket, setSelectedTicket] = useState(null);

    // Dashboard Filters & Export States
    const [searchQuery, setSearchQuery] = useState('');
    const [filterAssignee, setFilterAssignee] = useState(searchParams.get('assignee') || 'All'); 
    const [isRoutingModalOpen, setIsRoutingModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportFilters, setExportFilters] = useState({
        serverName: '', customerUsername: '', startDate: '', endDate: '', fetchAll: true
    });

    const prevTicketsRef = React.useRef([]);
    const isFirstLoad = React.useRef(true);

    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    const triggerNotification = (title, options) => {
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(title, options);
        }
    };

    useEffect(() => { 
        loadStaffData(); 
        
        // Native Firestore Real-time Listener for Tickets
        setLoading(true);
        let isMounted = true;
        const q = query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!isMounted) return;
            const fetchedTickets = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            if (!isFirstLoad.current) {
                snapshot.docChanges().forEach((change) => {
                    const newTicket = change.doc.data();
                    
                    if (change.type === 'added') {
                        triggerNotification('New Support Ticket', { body: `Ticket ${newTicket.ticketId}: ${newTicket.subject}` });
                    } else if (change.type === 'modified') {
                        const oldTicket = prevTicketsRef.current.find(t => t.id === change.doc.id);
                        if (oldTicket) {
                            // 2. Customer replies (unread update)
                            // We only trigger this if it's NOT a feedback submission to avoid duplicate notifications
                            const isNewFeedback = newTicket.feedback && (!oldTicket.feedback || oldTicket.feedback.submittedAt?.toMillis?.() !== newTicket.feedback.submittedAt?.toMillis?.());
                            
                            if (!oldTicket.hasSupportUnreadUpdate && newTicket.hasSupportUnreadUpdate && !isNewFeedback) {
                                triggerNotification('New Customer Update', { body: `Ticket ${newTicket.ticketId} has a new update.` });
                            }
                            
                            // 3. Feedback submitted
                            if (isNewFeedback) {
                                triggerNotification('Feedback Submitted', { body: `Customer rated Ticket ${newTicket.ticketId} with ${newTicket.feedback.rating} stars.` });
                            }
                            
                            // 4. Ticket re-opened
                            if ((oldTicket.status === 'Resolved' || oldTicket.status === 'Archived') && newTicket.status === 'Open') {
                                triggerNotification('Ticket Re-opened', { body: `Ticket ${newTicket.ticketId} was re-opened.` });
                            }
                        }
                    }
                });
            }

            prevTicketsRef.current = fetchedTickets;
            isFirstLoad.current = false;

            setTickets(fetchedTickets);
            setLoading(false);
            
            // Sync selected ticket if it's currently open
            setSelectedTicket(prev => {
                if (!prev) return null;
                const updatedTarget = fetchedTickets.find(t => t.id === prev.id);
                return updatedTarget || prev;
            });
        }, (error) => {
            console.error("Failed to stream tickets:", error);
            if (isMounted) setLoading(false);
        });

        return () => {
            isMounted = false;
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // This watches the URL and the tickets array to safely trigger deep-links
    useEffect(() => {
        let paramsChanged = false;

        const assigneeUid = searchParams.get('assignee');
        if (assigneeUid) {
            setFilterAssignee(assigneeUid);
            searchParams.delete('assignee');
            paramsChanged = true;
        }

        const linkTicketId = searchParams.get('ticketId');
        // Only attempt to open the ticket AFTER the tickets have safely downloaded
        if (linkTicketId && tickets.length > 0 && !selectedTicket) {
            const target = tickets.find(t => t.id === linkTicketId || t.ticketId === linkTicketId);
            if (target) {
                setSelectedTicket(target);
                // Consume the ticketId so it doesn't pop open again when new tickets stream in
                searchParams.delete('ticketId');
                paramsChanged = true;
            }
        }

        if (paramsChanged) {
            setSearchParams(searchParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, tickets]);

    const loadStaffData = async () => {
        try {
            const staffRes = await fetchSupportStaffAPI();
            setSupportStaff(staffRes.data.data || []);
            setStaff(staffRes.data.data || []);
        } catch (error) { console.error("Failed to load staff data:", error); } 
    };

    const handleDragStart = (e, ticketId) => e.dataTransfer.setData('ticketId', ticketId);
    const handleDragOver = (e) => e.preventDefault();
    
    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        const ticketId = e.dataTransfer.getData('ticketId');
        const ticket = tickets.find(t => t.id === ticketId);
        if (!ticket || ticket.status === newStatus) return;

        const oldStatus = ticket.status;
        setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
        try {
            await updateTicketStatusAPI(ticketId, newStatus);
            if (selectedTicket && selectedTicket.id === ticketId) {
                setSelectedTicket(prev => {
                    const updatedTicket = { ...prev, status: newStatus };
                    if (newStatus === 'Resolved') {
                        delete updatedTicket.followUp;
                    }
                    return updatedTicket;
                });
            }
        } catch (error) {
            // Rollback to the previous column on failure
            setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: oldStatus } : t));
            alert('Failed to move ticket: ' + (error.message || 'Unknown error'));
        }
    };

    // --- Execute Bulk Export with Filters ---
    const executeBulkExport = async () => {
        setIsExporting(true);
        try {
            const res = await fetchTicketsForExportAPI(exportFilters);
            const exportData = res.data.data || [];
            
            if (exportData.length === 0) {
                alert("No tickets found for these filters.");
                setIsExporting(false);
                return;
            }

            let content = `--- BULK TICKET EXPORT ---\nExport Date: ${new Date().toLocaleString()}\n\n`;
            
            for (const t of exportData) {
                content += `[${t.ticketId}] ${t.subject}\n`;
                content += `Server: ${t.reporter?.serverId || 'N/A'} | User: ${t.reporter?.username}\n`;
                content += `Status: ${t.status} | Priority: ${t.priority}\n`;
                content += `Assigned: ${t.assignedTo?.name || 'Unassigned'}\n`;
                content += `\n--- ORIGINAL REQUEST ---\n${t.description}\n`;
                
                content += `\n--- CHAT LOGS ---\n`;
                content += t.fullChatHistory ? `${t.fullChatHistory}\n` : `No replies.\n`;
                content += `\n------------------------------------------------------------\n\n`;
            }

            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Bulk_Support_Export_${new Date().getTime()}.txt`;
            a.click();
            URL.revokeObjectURL(url);
            setIsExportModalOpen(false);
            
        } catch (error) {
            console.error("Bulk export failed:", error);
            alert("Failed to compile bulk export.");
        } finally {
            setIsExporting(false);
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Urgent': return '#e53e3e';
            case 'High': return '#ed8936';
            case 'Medium': return '#3182ce';
            default: return '#718096';
        }
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'Bug/Crash': return <FaBug style={{ color: '#e53e3e' }} />;
            case 'Feature Request': return <FaLightbulb style={{ color: '#ecc94b' }} />;
            case 'Billing/Invoice': return <FaFileInvoiceDollar style={{ color: '#38a169' }} />;
            default: return <FaQuestionCircle style={{ color: '#a0aec0' }} />;
        }
    };

    if (loading) return <div style={{ padding: '20px' }}>Loading Support Dashboard...</div>;

    const getFilteredTickets = () => {
        return tickets.filter(ticket => {
            if (ticket.isRestrictedVisibility) {
                const isAssignedToMe = ticket.assignedTo?.uid === currentUser?.uid || (loginUsername && ticket.assignedTo?.uid === loginUsername);
                const isCollaborator = (ticket.collaborators || []).includes(currentUser?.uid) || (loginUsername && (ticket.collaborators || []).includes(loginUsername));
                const isSupportManagerCheck = userRole === 'Support Manager' || isSupportManager;
                const isDevCheck = userRole === 'Dev';
                if (!isAssignedToMe && !isCollaborator && !isSupportManagerCheck && !isDevCheck) return false; 
            }
            if (searchQuery.trim() !== '') {
                const query = searchQuery.toLowerCase();
                const matchSubject = (ticket.subject || '').toLowerCase().includes(query);
                const matchTicketId = (ticket.ticketId || '').toLowerCase().includes(query);
                const matchUsername = (ticket.reporter?.username || '').toLowerCase().includes(query);
                const matchServer = (ticket.reporter?.serverId || '').toLowerCase().includes(query);
                if (!matchSubject && !matchTicketId && !matchUsername && !matchServer) return false;
            }
            if (filterAssignee !== 'All') {
                if (filterAssignee === 'Unassigned' && ticket.assignedTo != null) return false;
                const isMyFilter = filterAssignee === 'me' || filterAssignee === currentUser?.uid || (loginUsername && filterAssignee === loginUsername);
                if (isMyFilter) {
                    if (ticket.assignedTo?.uid !== currentUser?.uid && ticket.assignedTo?.uid !== loginUsername) return false;
                } else if (filterAssignee !== 'Unassigned') {
                    if (ticket.assignedTo?.uid !== filterAssignee) return false;
                }
            }
            return true;
        });
    };

    const displayTickets = getFilteredTickets();

    return (
        <div style={{ padding: '20px', backgroundColor: 'var(--page-bg)', minHeight: '100vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h1 style={{ color: 'var(--title-text)', fontSize: '24px', margin: 0 }}>Support Tickets</h1>
            </div>

            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="text" placeholder="Search by ID, Username, Server, or Subject..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ flex: 1, minWidth: '250px', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0' }} />
                <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} style={{ padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', backgroundColor: 'white' }}>
                    <option value="All">All Tickets</option>
                    <option value="Unassigned">Unassigned Only</option>
                    <option value="me">Assigned to Me</option>
                    {staff.filter(s => s.uid !== currentUser?.uid && s.uid !== loginUsername).map(user => <option key={user.uid} value={user.uid}>{user.name}</option>)}
                </select>
                {isSupportManager && (
                    <button onClick={() => setIsRoutingModalOpen(true)} style={{ padding: '10px 15px', backgroundColor: '#2b6cb0', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>Routing Rules</button>
                )}
                <button onClick={() => setIsExportModalOpen(true)} style={{ padding: '10px 15px', backgroundColor: '#4a5568', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <FaDownload /> Bulk Export
                </button>
            </div>

            <div style={{ display: 'flex', gap: '15px', overflowX: 'auto', paddingBottom: '15px', height: 'calc(100vh - 170px)', alignItems: 'stretch' }}>
                {COLUMNS.map(column => {
                    const columnTickets = displayTickets.filter(t => t.status === column.id);
                    return (
                        <div key={column.id} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, column.id)} style={{ minWidth: '320px', width: '320px', backgroundColor: column.color, borderRadius: '8px', padding: '12px', borderTop: `4px solid ${column.bar}`, display: 'flex', flexDirection: 'column', maxHeight: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#4a5568', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                                {column.title} <span style={{ backgroundColor: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>{columnTickets.length}</span>
                            </h3>
                            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '4px' }}>
                                {columnTickets.map(ticket => (
                                    <div key={ticket.id} draggable onDragStart={(e) => handleDragStart(e, ticket.id)} onClick={() => setSelectedTicket(ticket)} style={{ backgroundColor: ticket.hasSupportUnreadUpdate ? '#fff5f5' : 'white', padding: '15px', borderRadius: '6px', boxShadow: ticket.hasSupportUnreadUpdate ? '0 0 0 2px #fc8181' : '0 1px 3px rgba(0,0,0,0.1)', cursor: 'grab', borderLeft: `3px solid ${getPriorityColor(ticket.priority)}`, transition: 'all 0.2s' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#718096' }}>{ticket.ticketId}</span>
                                                {/* Visual Badges connected to Backend State Machine */}
                                                {ticket.hasSupportUnreadUpdate && (
                                                    <span style={{ backgroundColor: '#e53e3e', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '9px', fontWeight: 'bold', letterSpacing: '0.5px' }}>ACTION REQUIRED</span>
                                                )}
                                                {ticket.hasNewCustomerReply && (
                                                    <span style={{ backgroundColor: '#d69e2e', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '9px', fontWeight: 'bold', letterSpacing: '0.5px' }}>NEW REPLY</span>
                                                )}
                                            </div>
                                            <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', backgroundColor: '#edf2f7', color: '#4a5568', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>{getCategoryIcon(ticket.category)} {ticket.category}</span>
                                        </div>
                                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#2d3748' }}>{ticket.subject}</h4>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', borderTop: '1px solid #edf2f7', paddingTop: '10px' }}>
                                            <span style={{ fontSize: '12px', color: '#a0aec0' }}>{ticket.reporter?.username}</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {ticket.feedback?.rating && (
                                                    <span style={{ fontSize: '10px', backgroundColor: '#fefcbf', color: '#975a16', padding: '2px 6px', borderRadius: '10px', fontWeight: 'bold' }}>★ {ticket.feedback.rating}/5</span>
                                                )}
                                                {ticket.followUp && ticket.followUp.status !== 'completed' && (
                                                    <span style={{ fontSize: '9px', backgroundColor: '#fed7d7', color: '#9b2c2c', padding: '2px 6px', borderRadius: '10px', fontWeight: 'bold' }}>QA REVIEW</span>
                                                )}
                                                {ticket.assignedTo ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', backgroundColor: '#e6fffa', color: '#234e52', padding: '2px 8px', borderRadius: '12px' }}><FaUserTag /> {(ticket.assignedTo?.name || 'Staff').split(' ')[0]}</div>
                                                ) : (
                                                    <span style={{ fontSize: '11px', color: '#a0aec0', fontStyle: 'italic' }}>Unassigned</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {selectedTicket && (
                <TicketDetailModal 
                    ticket={selectedTicket}
                    onClose={() => setSelectedTicket(null)}
                    currentUser={currentUser}
                    staff={staff}
                    supportStaff={supportStaff}
                    onTicketUpdated={(updatedTicket) => {
                        setSelectedTicket(updatedTicket);
                        setTickets(prev => prev.map(t => t.id === updatedTicket.id ? updatedTicket : t));
                    }}
                />
            )}
            
            {isRoutingModalOpen && <RoutingRulesModal closeModal={() => setIsRoutingModalOpen(false)} staff={staff} />}

            {/* --- Bulk Export Modal UI --- */}
            {isExportModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 }}>
                    <div style={{ backgroundColor: 'white', width: '500px', padding: '25px', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '20px', color: '#2d3748', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FaDownload style={{ color: '#4a5568' }} /> Export Tickets
                        </h2>
                        
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', marginBottom: '5px', color: '#4a5568' }}>Data Scope</label>
                            <select 
                                value={exportFilters.fetchAll ? 'all' : 'filtered'} 
                                onChange={(e) => setExportFilters({ ...exportFilters, fetchAll: e.target.value === 'all' })}
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', backgroundColor: '#f7fafc' }}
                            >
                                <option value="all">Export ALL Historical Tickets</option>
                                <option value="filtered">Custom Filters (Date Range, Server, User)</option>
                            </select>
                        </div>

                        {!exportFilters.fetchAll && (
                            <div style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#718096' }}>Start Date</label>
                                        <input type="date" value={exportFilters.startDate} onChange={e => setExportFilters({...exportFilters, startDate: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0', boxSizing: 'border-box' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#718096' }}>End Date</label>
                                        <input type="date" value={exportFilters.endDate} onChange={e => setExportFilters({...exportFilters, endDate: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0', boxSizing: 'border-box' }} />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#718096' }}>Filter by Server ID (Optional)</label>
                                    <input type="text" placeholder="e.g. healthautomated.care" value={exportFilters.serverName} onChange={e => setExportFilters({...exportFilters, serverName: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0', boxSizing: 'border-box' }} />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#718096' }}>Filter by Customer Username (Optional)</label>
                                    <input type="text" placeholder="e.g. john_doe" value={exportFilters.customerUsername} onChange={e => setExportFilters({...exportFilters, customerUsername: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e0', boxSizing: 'border-box' }} />
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                            <button onClick={() => setIsExportModalOpen(false)} style={{ padding: '10px 15px', backgroundColor: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
                            <button onClick={executeBulkExport} disabled={isExporting} style={{ padding: '10px 15px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isExporting ? <><FaSpinner className="fa-spin" /> Generating...</> : 'Generate Export'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}